import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './OnboardingTour.css';

const OnboardingTour = ({ isOpen, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const steps = [
    {
      title: 'Bem-vindo ao MicroGestor!',
      icon: 'bi-stars',
      description: 'Vamos fazer um tour rápido para você conhecer as principais funcionalidades.',
      target: null,
      position: 'center',
      path: '/dashboard'
    },
    {
      title: 'Dashboard',
      icon: 'bi-speedometer2',
      description: 'Aqui você tem uma visão geral do seu negócio: vendas, lucros, pedidos e muito mais em tempo real.',
      target: '[data-tour="dashboard"]',
      position: 'bottom',
      path: '/dashboard'
    },
    {
      title: 'Produtos',
      icon: 'bi-box-seam',
      description: 'Cadastre seus produtos, controle estoque, defina preços e acompanhe a margem de lucro automaticamente.',
      target: '[data-tour="products"]',
      position: 'bottom',
      path: '/produtos'
    },
    {
      title: 'Pedidos',
      icon: 'bi-clipboard-check',
      description: 'Gerencie pedidos de clientes, acompanhe status e compartilhe links públicos para seus clientes acompanharem.',
      target: '[data-tour="orders"]',
      position: 'bottom',
      path: '/pedidos'
    },
    {
      title: 'Despesas',
      icon: 'bi-wallet2',
      description: 'Controle todas as suas despesas, categorize e tenha clareza total sobre seus custos mensais.',
      target: '[data-tour="expenses"]',
      position: 'bottom',
      path: '/despesas'
    },
    {
      title: 'Clientes',
      icon: 'bi-people',
      description: 'Cadastre clientes, veja histórico de compras e mantenha relacionamento organizado.',
      target: '[data-tour="clients"]',
      position: 'bottom',
      path: '/clientes'
    },
    {
      title: 'Relatórios',
      icon: 'bi-bar-chart-line',
      description: 'Análises detalhadas, gráficos e relatórios para tomar decisões baseadas em dados reais.',
      target: '[data-tour="reports"]',
      position: 'bottom',
      path: '/relatorios'
    },
    {
      title: 'Pronto para começar!',
      icon: 'bi-check-circle',
      description: 'Você está pronto! Comece cadastrando seus primeiros produtos e fazendo sua primeira venda.',
      target: null,
      position: 'center',
      path: '/produtos'
    }
  ];

  useEffect(() => {
    if (isOpen && steps[currentStep]) {
      const currentStepData = steps[currentStep];

      // Navegar para a página correspondente
      if (currentStepData.path && location.pathname !== currentStepData.path) {
        navigate(currentStepData.path);
      }

      // Scroll para o elemento após navegação
      if (currentStep > 0 && currentStep < steps.length - 1 && currentStepData.target) {
        setTimeout(() => {
          const targetElement = document.querySelector(currentStepData.target);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300); // Delay para garantir que a página carregou
      }
    }
  }, [currentStep, isOpen, navigate, location.pathname]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setCurrentStep(0);
    onComplete();
  };

  if (!isOpen) return null;

  const currentStepData = steps[currentStep];
  const isCentered = currentStepData.position === 'center';

  return (
    <>
      {/* Overlay */}
      <div className="onboarding-overlay" onClick={handleSkip} />

      {/* Spotlight para o elemento atual */}
      {!isCentered && currentStepData.target && (
        <div className="onboarding-spotlight" data-target={currentStepData.target} />
      )}

      {/* Card do tour */}
      <div className={`onboarding-card ${isCentered ? 'centered' : ''}`} data-step={currentStep}>
        <div className="onboarding-header">
          <div className="onboarding-title">
            <i className={`bi ${currentStepData.icon}`}></i>
            <h3>{currentStepData.title}</h3>
          </div>
          <button className="onboarding-close" onClick={handleSkip} aria-label="Fechar tour">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="onboarding-body">
          <p>{currentStepData.description}</p>
        </div>

        <div className="onboarding-footer">
          <div className="onboarding-progress">
            <span className="step-counter">{currentStep + 1} de {steps.length}</span>
            <div className="progress-dots">
              {steps.map((_, index) => (
                <span
                  key={index}
                  className={`dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                />
              ))}
            </div>
          </div>

          <div className="onboarding-actions">
            {currentStep > 0 && (
              <button className="btn btn-outline-secondary" onClick={handlePrevious}>
                Anterior
              </button>
            )}

            {currentStep < steps.length - 1 ? (
              <>
                <button className="btn btn-link text-muted" onClick={handleSkip}>
                  Pular tour
                </button>
                <button className="btn btn-primary" onClick={handleNext}>
                  Próximo
                </button>
              </>
            ) : (
              <button className="btn btn-success" onClick={handleComplete}>
                <i className="bi bi-check-circle me-2"></i>
                Começar a usar
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingTour;
